/* Signing up, in and out, and keeping "me" fresh. */
import { supa, call, api, backendReady } from './supa'
import { useMe, lookOf, type Me } from './session'
import { load, save } from '../lib/storage'

/* eslint-disable @typescript-eslint/no-explicit-any */
function toMe(j: any): Me {
  return {
    id: j.id, username: j.username, displayName: j.display_name, avatar: j.avatar_url, icon: j.icon, color: j.color, bio: j.bio || '',
    xp: Number(j.xp) || 0, coins: Number(j.coins) || 0, premiumUntil: j.premium_until, premium: !!j.premium, isAdmin: !!j.is_admin,
    streak: j.streak || 0, streakLast: j.streak_last, bestStreak: j.best_streak || 0, shields: j.shields || 0, createdAt: j.created_at,
    equipped: j.equipped || {}, badges: j.badges || [], gamesPlayed: j.games_played || 0,
    roundsPlayed: j.rounds_played || 0, totalPoints: Number(j.total_points) || 0, perfectRounds: j.perfect_rounds || 0, bestGame: j.best_game || 0,
    email: j.email, birthYear: j.birth_year, birthMonth: j.birth_month, tokens: j.tokens || {}, giftDay: j.gift_day, counters: j.counters || {}, today: j.today,
    referredBy: j.referred_by, referrals: j.referrals || 0, subscription: j.subscription || null,
  }
}

let ticked = false
export async function refreshMe(): Promise<Me | null> {
  if (!backendReady) {
    useMe.setState({ loaded: true })
    return null
  }
  try {
    const { data } = await supa.auth.getSession()
    if (!data.session) {
      useMe.setState({ loaded: true, me: null, look: lookOf(null) })
      return null
    }
    const j = await call<any>('me')
    if (!j) {
      // an auth user without a profile is treated as signed out
      useMe.setState({ loaded: true, me: null, look: lookOf(null) })
      return null
    }
    const me = toMe(j)
    useMe.setState({ loaded: true, me, look: lookOf(me) })
    save('lastMe', { username: me.username, color: me.color })
    if (!ticked) {
      ticked = true
      call<{ premium_coins: number }>('daily_tick').then((t) => { if (t?.premium_coins) void refreshMe() }).catch(() => {})
    }
    return me
  } catch {
    useMe.setState({ loaded: true })
    return useMe.getState().me
  }
}

export function patchMe(p: Partial<Me>) {
  const me = useMe.getState().me
  if (!me) return
  const next = { ...me, ...p }
  useMe.setState({ me: next, look: lookOf(next) })
}

export async function initAuth() {
  useMe.setState({ adminOn: load('adminOn', true) })
  await refreshMe()
  supa.auth.onAuthStateChange((ev) => {
    if (ev === 'SIGNED_OUT') useMe.setState({ me: null, look: lookOf(null) })
    else if (ev === 'SIGNED_IN' || ev === 'USER_UPDATED') void refreshMe()
  })
}

interface SessionTokens { access_token: string; refresh_token: string }
async function adopt(session: SessionTokens | null | undefined) {
  if (!session) throw new Error('server')
  const { error } = await supa.auth.setSession({ access_token: session.access_token, refresh_token: session.refresh_token })
  if (error) throw new Error('server')
  return refreshMe()
}

export interface SignupFields { username: string; password: string; display_name?: string; email?: string; birth_year: number; birth_month: number; color?: string; icon?: string; ref?: string; legacy?: string }
/* A v3 account that signs in for the first time: it needs a birthday before it comes over. */
export interface Claim { handle: string; username: string; name: string; color: string | null }
export async function signUp(f: SignupFields) {
  const r = await api<{ session: SessionTokens }>('auth/signup', f)
  return adopt(r.session)
}
export async function signIn(login: string, password: string): Promise<{ claim: Claim } | null> {
  const r = await api<{ session?: SessionTokens; claim?: Claim }>('auth/login', { login, password })
  if (r.claim) return { claim: r.claim }
  await adopt(r.session!)
  return null
}
export async function requestReset(login: string) {
  await api('auth/reset', { login })
}
export async function confirmReset(token: string, password: string) {
  const r = await api<{ session: SessionTokens }>('auth/reset-confirm', { token, password })
  return adopt(r.session)
}
export async function changePassword(current: string, password: string) {
  const r = await api<{ session: SessionTokens }>('auth/account', { action: 'password', current, password })
  return adopt(r.session)
}
export async function setResetEmail(email: string) {
  await api('auth/account', { action: 'email', email })
  return refreshMe()
}
export async function deleteAccount(confirm: string, password: string) {
  await api('auth/account', { action: 'delete', confirm, password })
  await supa.auth.signOut()
  useMe.setState({ me: null, look: lookOf(null) })
}
export async function signOut() {
  await supa.auth.signOut()
  useMe.setState({ me: null, look: lookOf(null) })
}
export function setAdminOn(on: boolean) {
  save('adminOn', on)
  useMe.setState({ adminOn: on })
}
